import {
  FormField,
  SpaceBetween,
  Header,
  Button,
  Spinner,
  Alert,
  BreadcrumbGroupProps,
  SelectProps,
} from '@amzn/awsui-components-react-v3';
import { useLazyQuery, useQuery } from '@apollo/client';
import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FileSaver from 'file-saver';
import { usePapaParse } from 'react-papaparse';
import _ from 'lodash';
import ContainerSearch, {
  SearchVariables,
} from '../../../components/ContainerManagement/ContainerSearch';
import {
  GET_CMS_LIST_SITES,
  GET_CONTAINER_PERMISSIONS,
  GET_LIST_CONTAINER_DEFINITION,
  GET_LOCATIONS_FOR_SITE,
  LIST_CONTAINER_TYPES,
  MANAGE_CONTAINER_PERMISSIONS,
  SEARCH_CONTAINER,
} from '../../../services/container-management/queries';
import { LocationInfo, LocationProps } from '../../../services/container-management/types';
import { ContainerDefinitionResults, ContainerSearchResult } from '../types';
import status from '../mocks/status.json';
import compliance from '../mocks/compliance.json';
import isEmpty from '../mocks/isEmpty.json';
import ContainerSearchResultsTable, {
  SearchOptions,
} from '../../../components/ContainerManagement/ContainerSearchResultsTable';
import Logger from '../../../utils/Logger';
import {
  getBlocksFromRules,
  getStatus,
  transformDataForMultiselectDropdown,
} from '../../../utils/ContainerManagement/containerSearchHelper';
import {
  getCategoryOptions,
  optionComparator,
  parseListContainerDefinitionsResponse,
} from '../../../utils/ContainerManagement/containerDefinitionsHelper';
import UserPreference from '../../../components/UserPreference';
import zoneOptions from '../mocks/zoneOptions.json';
import { zoneFeatureFlag, capacityFilterFlag } from '../../../utils/featureFlags';

type DownloadBatch = {
  containers: ContainerSearchResult[];
  index: number;
  siteId: string;
  siteIds: string[];
  state: string;
  isCompliant: string;
  isEmpty: string;
  zone: string;
  searchString: string;
  type: string;
  category: string;
  subcategory: string;
  roomId: string;
  parentContainerType: string;
  parentContainerName: string;
  userAlias: string;
  sortingColumn: string;
  sortingDescending: string;
  totalResults: number;
};

export default function ContainerManagement(props: {
  onBreadcrumbChange: (newBreadcrumbs: BreadcrumbGroupProps.Item[]) => void;
}): ReactElement {
  const { t } = useTranslation();
  const { jsonToCSV } = usePapaParse();
  const [containerTypeOptions, setContainerTypeOptions] = React.useState<SelectProps.Option[]>([]);
  const [parentContainerTypeOptions, setParentContainerTypeOptions] = React.useState<
    SelectProps.Option[]
  >([]);
  const [listContainerTypesError, setListContainerTypesError] = React.useState<string>('');
  const [getContainerPermissions, setGetContainerPermissions] = useState(false);
  const [manageContainerPermissions, setManageContainerPermissions] = useState(false);
  const [roomOptions, setRoomOptions] = useState<any>([]);
  const [containerTypeForSearch, setContainerTypeForSearch] = useState<string>('');
  const [containerTypeForTable, setContainerTypeForTable] = useState<string>('');
  const [siteChangeForSearch, setSiteChangeForSearch] = useState<[LocationProps]>();
  const [searchVariables, setSearchVariables] = useState<SearchVariables>();
  const [searchOptions, setSearchOptions] = useState<SearchOptions>({
    pageSize: 10,
    sortingColumn: '',
    sortingDescending: false,
  });
  const [pageIndex, setPageIndex] = useState<number>(1);
  const [searchResults, setSearchResults] = useState<any>(null);
  const [totalResults, setTotalResults] = useState<number>(0);
  const [locations, setLocations] = useState([]);
  const [siteId] = useState(
    JSON.parse(localStorage.getItem('siteId') ?? '{"locationId": ""}').locationId
  );
  const [downloadBatch, setDownloadBatch] = useState<DownloadBatch | null>(null);
  const [typeToContainerDefinitionResults, setTypeToContainerDefinitionResults] = useState<
    Map<string, ContainerDefinitionResults>
  >(new Map());
  const zoneFeatureLaunched = zoneFeatureFlag();
  const capacityFilterLaunched = capacityFilterFlag();

  const DOWNLOAD_BATCH_SIZE = 1_000;

  const breadcrumb = [
    {
      text: t('breadcrumb.home'),
      href: '/',
    },
    {
      text: t('Inventory Management'),
      href: '/assets-inventory/',
    },
    {
      text: t('Container Management'),
      href: '/container/manage/',
    },
  ];

  const [listSites, { loading: listSitesLoading, error: listSitesError }] = useLazyQuery(
    GET_CMS_LIST_SITES,
    {
      fetchPolicy: 'network-only',
      onCompleted: (result) => {
        const sortedSites = result.listSites.locations
          .slice()
          .sort(function (a: LocationInfo, b: LocationInfo) {
            return a.locationName.localeCompare(b.locationName, undefined, {
              numeric: true,
              sensitivity: 'base',
            });
          });
        setLocations(transformDataForMultiselectDropdown(sortedSites));
      },
    }
  );

  const [
    callManageContainerPermissions,
    { loading: manageContainerPermissionsLoading, error: manageContainerPermissionsError },
  ] = useLazyQuery(MANAGE_CONTAINER_PERMISSIONS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onCompleted: (result) => {
      setManageContainerPermissions(result?.manageContainerPermissionsCheck?.user);
    },
  });

  const [
    getLocationsForSite,
    { loading: getLocationsForSiteLoading, error: getLocationsForSiteError },
  ] = useLazyQuery(GET_LOCATIONS_FOR_SITE, {
    fetchPolicy: 'network-only',
    onCompleted: (result) => {
      const locs: LocationInfo[] = result.getLocationsForSite.locations;
      setRoomOptions(
        locs
          .map((loc: LocationInfo) => {
            return {
              label: loc.locationName,
              value: loc.locationId,
            };
          })
          .sort(optionComparator)
      );
    },
  });

  // Query listContainerTypes
  const [listContainerTypes, { loading: listContainerTypesLoading }] = useLazyQuery(
    LIST_CONTAINER_TYPES,
    {
      fetchPolicy: 'network-only',
      onCompleted: (result) => {
        if (result?.listContainerTypes?.containerTypes == null) {
          setListContainerTypesError('Error listing container types: result is null');
        } else {
          const types = result.listContainerTypes.containerTypes.map((type: string) => {
            return {
              label: t(`container-management.${type}`),
              value: type,
            };
          });
          setContainerTypeOptions(types);
          setParentContainerTypeOptions(types);
          setListContainerTypesError('');
        }
      },
    }
  );

  const [
    listContainerDefinitions,
    { loading: listContainerDefinitionsLoading, error: listContainerDefinitionsError },
  ] = useLazyQuery(GET_LIST_CONTAINER_DEFINITION, {
    fetchPolicy: 'network-only',
    onCompleted: (result) => {
      const [categoryMapResult, containerDefinitionMapResult] =
        parseListContainerDefinitionsResponse(
          result.listContainerDefinitions.containerType,
          result.listContainerDefinitions.categories
        );
      setTypeToContainerDefinitionResults(
        _.cloneDeep(
          typeToContainerDefinitionResults.set(result.listContainerDefinitions.containerType, {
            categoryMap: categoryMapResult,
            containerDefinitionMap: containerDefinitionMapResult,
          })
        )
      );
    },
  });

  const [searchContainer, { loading: searchContainerLoading, error: searchContainerError }] =
    useLazyQuery(SEARCH_CONTAINER, {
      fetchPolicy: 'network-only',
      onCompleted: (result) => {
        setSearchResults(
          result.searchContainer.documents?.map((document: any) => document?.data) ?? []
        );
        setTotalResults(result.searchContainer.totalResults);
      },
    });

  const [
    downloadContainers,
    { loading: downloadContainersLoading, error: downloadContainersError },
  ] = useLazyQuery(SEARCH_CONTAINER, {
    fetchPolicy: 'network-only',
    onCompleted: (result) => {
      if (downloadBatch) {
        setDownloadBatch({
          ...downloadBatch,
          containers: [
            ...downloadBatch.containers,
            ...(result.searchContainer.documents?.map((document: any) => document?.data) ?? []),
          ],
          index: downloadBatch.index + 1,
        });
      }
    },
  });

  const downloadContainersToCSV = (containers: ContainerSearchResult[]) => {
    const now = new Date();
    const csvFileName =
      `ContainerSearchResults-${now.getFullYear()}-${now.getMonth()}-${now.getDate()}-` +
      `${now.getHours().toString()}-${now.getMinutes().toString()}-${now.getSeconds().toString()}`;
    const rows = containers.map((item: any) => {
      return {
        'Container ID': item.containerId,
        Name: item.name,
        Type: item.containerType,
        Category: item.category,
        Subcategory: item.subcategory,
        Location: [item.site?.name, item.room?.name, item.section, item.aisle, item.level]
          .filter(Boolean)
          .join('.')
          .replace(/\s+/g, ''),
        Rules: getBlocksFromRules(item.policy?.rule),
        State: getStatus(item.state),
        Size: item.size,
        'Seal IDs': item.seal_ids,
        'Ticket IDs': item.ticket_ids,
        'CCTV Coverage': `${item.cctvRequired === 'true' ? 'Yes' : 'No'}`,
        'Created By': item.createdBy,
        'Last Updated By': item.lastUpdatedUser,
        'Creation Time': item.creationTime,
        'Last Update Time': item.lastUpdatedTime,
        'Parent Container Name': item.parent_container.fq_name,
        'Parent Container Type': item.parent_container.type,
        'User With Custody': item.userAlias,
        'Overridden Rules': `${item.hasOverriddenRules === true ? 'Yes' : 'No'}`,
        Compliant: `${item.isCompliant === true ? 'Yes' : 'No'}`,
        ...(zoneFeatureLaunched && { Zone: item.room.zone }),
        ...(capacityFilterLaunched && {
          'Container capacity': `${item.isEmpty === true ? 'Empty' : 'Contains parts'}`,
        }),
      };
    });
    const blob = new Blob([`${jsonToCSV(rows)}`], { type: 'text/csv;charset=utf-8;' });
    FileSaver.saveAs(blob, `${csvFileName}.csv`);

    setDownloadBatch(null);
  };

  const downloadAllContainers = () => {
    setDownloadBatch({
      containers: [],
      index: 0,
      siteId: '', // TODO: Remove this after CMS is fully migrated to using siteIds
      siteIds: searchVariables?.siteIds ?? [],
      isCompliant: searchVariables?.isCompliant ?? '',
      isEmpty: searchVariables?.isEmpty ?? '',
      state: searchVariables?.state ?? '',
      zone: searchVariables?.zone ?? '',
      searchString: searchVariables?.searchString ?? '',
      type: searchVariables?.type ?? '',
      category: searchVariables?.category ?? '',
      subcategory: searchVariables?.subcategory ?? '',
      parentContainerType: searchVariables?.parentContainerType ?? '',
      parentContainerName: searchVariables?.parentContainerName ?? '',
      userAlias: searchVariables?.userAlias ?? '',
      roomId: searchVariables?.room ?? '',
      sortingColumn: searchOptions?.sortingColumn ?? '',
      sortingDescending: searchOptions?.sortingDescending ? 'desc' : 'asc',
      totalResults,
    });
  };

  useEffect(() => {
    if (downloadBatch) {
      if (downloadBatch.index >= downloadBatch.totalResults / DOWNLOAD_BATCH_SIZE) {
        downloadContainersToCSV(downloadBatch.containers);
      } else {
        const variables = {
          siteId: downloadBatch.siteId,
          siteIds: downloadBatch.siteIds,
          isCompliant: searchVariables?.isCompliant ?? '',
          isEmpty: searchVariables?.isEmpty ?? '',
          zone: searchVariables?.zone ?? '',
          state: searchVariables?.state ?? '',
          searchString: downloadBatch.searchString,
          type: downloadBatch.type,
          category: downloadBatch.category,
          subcategory: downloadBatch.subcategory,
          parentContainerType: downloadBatch.parentContainerType,
          parentContainerName: downloadBatch.parentContainerName,
          userAlias: downloadBatch.userAlias,
          roomId: downloadBatch.roomId,
          from: downloadBatch.index * DOWNLOAD_BATCH_SIZE,
          size: DOWNLOAD_BATCH_SIZE,
          sortOptions: downloadBatch.sortingColumn
            ? [
                {
                  sortKey: downloadBatch.sortingColumn,
                  sortOrder: downloadBatch.sortingDescending ? 'desc' : 'asc',
                },
              ]
            : null,
        };

        Logger.debug(`Downloading containers with ${JSON.stringify(variables)}`);

        downloadContainers({
          variables,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadBatch]);

  const callSearchContainer = () => {
    if (searchVariables) {
      const variables = {
        siteId,
        searchString: searchVariables.searchString,
        type: searchVariables.type,
        category: searchVariables.category,
        subcategory: searchVariables.subcategory,
        roomId: searchVariables.room,
        state: searchVariables.state,
        isCompliant: searchVariables.isCompliant,
        isEmpty: searchVariables.isEmpty,
        zone: searchVariables.zone,
        parentContainerType: searchVariables.parentContainerType,
        parentContainerName: searchVariables.parentContainerName,
        userAlias: searchVariables.userAlias,
        from: (pageIndex - 1) * searchOptions.pageSize,
        size: searchOptions?.pageSize,
        siteIds: searchVariables.siteIds,
        sortOptions: searchOptions.sortingColumn
          ? [
              {
                sortKey: searchOptions.sortingColumn,
                sortOrder: searchOptions.sortingDescending ? 'desc' : 'asc',
              },
            ]
          : null,
      };

      Logger.debug(`Calling Container Search with ${JSON.stringify(variables)}}`);
      searchContainer({
        variables,
      });
    }
  };

  useEffect(() => {
    callSearchContainer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex]);

  useEffect(() => {
    if (pageIndex !== 1) {
      setPageIndex(1);
    } else {
      setSearchOptions({
        pageSize: searchOptions.pageSize,
        sortingColumn: '',
        sortingDescending: false,
      });
      callSearchContainer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchVariables]);

  useEffect(() => {
    if ((pageIndex - 1) * searchOptions.pageSize >= totalResults) {
      setPageIndex(1);
    } else {
      callSearchContainer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchOptions]);

  const { loading: getContainerPermissionsLoading, error: getContainerPermissionsError } = useQuery(
    GET_CONTAINER_PERMISSIONS,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
      onCompleted: (result) => {
        setGetContainerPermissions(result?.getContainerPermissionsCheck?.user);
      },
    }
  );

  useEffect(() => {
    listSites();
    listContainerTypes();
    props.onBreadcrumbChange(breadcrumb);
    callManageContainerPermissions();
    if (siteId) {
      getLocationsForSite({ variables: { locationId: siteId } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (siteId) {
      getLocationsForSite({ variables: { locationId: siteId } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  useEffect(() => {
    if (containerTypeForSearch && !typeToContainerDefinitionResults.has(containerTypeForSearch)) {
      listContainerDefinitions({ variables: { containerType: containerTypeForSearch } });
    }
    if (containerTypeForTable && !typeToContainerDefinitionResults.has(containerTypeForTable)) {
      listContainerDefinitions({ variables: { containerType: containerTypeForTable } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerTypeForSearch, containerTypeForTable]);

  useEffect(() => {
    if (siteChangeForSearch?.length === 1) {
      getLocationsForSite({ variables: { locationId: siteChangeForSearch[0].locationId } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteChangeForSearch]);

  if (!getContainerPermissions && !manageContainerPermissions) {
    const errorMessage =
      "You don't have Permissions to access Container Management System. " +
      'Permissions are provided on a need to have basis and determined by logistics manager and GPO. ' +
      'Please contact your manager who will request access from GPO.';
    return (
      <div>
        {(getContainerPermissionsLoading || manageContainerPermissionsLoading) && (
          <Spinner size="big" />
        )}
        {getContainerPermissionsError && <Alert type="error">{errorMessage}</Alert>}
      </div>
    );
  }

  return (
    <div>
      <SpaceBetween direction="vertical" size="s">
        {(listContainerTypesLoading ||
          listContainerDefinitionsLoading ||
          getLocationsForSiteLoading ||
          manageContainerPermissionsLoading ||
          searchContainerLoading ||
          listSitesLoading ||
          downloadContainersLoading) && <Spinner size="big" />}
        {manageContainerPermissionsError && (
          <Alert type="info">
            {"You don't have Permissions to access Container Management System. " +
              'Permissions are provided on a need to have basis and determined by logistics manager and GPO. ' +
              'Please contact your manager who will request access from GPO.'}
          </Alert>
        )}
        {listContainerTypesError && (
          <Alert type="error">Error listing container types: {listContainerTypesError}</Alert>
        )}
        {listContainerDefinitionsError && (
          <Alert type="error">
            Error listing container definitions: {listContainerDefinitionsError.message}
          </Alert>
        )}
        {getLocationsForSiteError && (
          <Alert type="error">GetLocationsForSite: {getLocationsForSiteError.message}</Alert>
        )}
        {searchContainerError && (
          <Alert type="error">SearchContainer: {searchContainerError.message}</Alert>
        )}
        {downloadContainersError && (
          <Alert type="error">DownloadContainers: {downloadContainersError.message}</Alert>
        )}
        {listSitesError && <Alert type="error">DownloadContainers: {listSitesError.message}</Alert>}
        <>
          <Header
            variant="h1"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <UserPreference />
                <FormField>
                  <Button
                    href="/assets-inventory/container/create/"
                    variant="primary"
                    disabled={!manageContainerPermissions}>
                    {t('+ Create')}
                  </Button>
                </FormField>
              </SpaceBetween>
            }>
            {t('Container Management')}
          </Header>
          <ContainerSearch
            siteId={siteId}
            typeOptions={containerTypeOptions}
            statusOptions={status}
            complianceOptions={compliance}
            isEmptyOptions={isEmpty}
            zoneOptions={zoneOptions}
            parentContainerTypeOptions={parentContainerTypeOptions}
            categoryOptions={getCategoryOptions(
              typeToContainerDefinitionResults.get(containerTypeForSearch)?.categoryMap ?? null
            )}
            categoriesMap={
              typeToContainerDefinitionResults.get(containerTypeForSearch)?.categoryMap ?? null
            }
            roomOptions={roomOptions}
            handleTypeChange={setContainerTypeForSearch}
            setSearchVariables={setSearchVariables}
            handleSiteChange={setSiteChangeForSearch}
            locationOptions={locations}
          />
          {searchResults && (
            <ContainerSearchResultsTable
              searchResults={searchResults}
              totalResults={totalResults}
              pageIndex={pageIndex}
              setPageIndex={setPageIndex}
              setSearchOptions={setSearchOptions}
              downloadSelectedContainers={downloadContainersToCSV}
              downloadAllContainers={downloadAllContainers}
              categoryOptions={getCategoryOptions(
                typeToContainerDefinitionResults.get(containerTypeForTable)?.categoryMap ?? null
              )}
              categoriesMap={
                typeToContainerDefinitionResults.get(containerTypeForTable)?.categoryMap ?? null
              }
              containerDefinitionMap={
                typeToContainerDefinitionResults.get(containerTypeForTable)
                  ?.containerDefinitionMap ?? null
              }
              managePermissions={!!manageContainerPermissions}
              handleTypeChange={setContainerTypeForTable}
              searchVariables={searchVariables}
            />
          )}
        </>
      </SpaceBetween>
    </div>
  );
}
